<script>
import { mapActions, mapState } from 'vuex';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import ImportResidentModal from './ImportResidentModal.vue';
import { useAlert } from 'dashboard/composables';
import TableFooter from '../../../../components/widgets/TableFooter.vue';
import ExportResidentModal from './ExportResidentModal.vue';
import { debounce } from '@chatwoot/utils';
import DeleteResidentModal from './DeleteResidentModal.vue';
import EditResidentModal from './EditResidentModal.vue';

export default {
  name: "ResidentList",
  components: {
    LoadingState,
    ImportResidentModal,
    TableFooter,
    ExportResidentModal,
    DeleteResidentModal,
    EditResidentModal,
  },
  data() {
    return {
      searchQuery: '',
      selectedSortOption: 'unit_asc',
      selectedStatus: 'loading',
      sortOptions: [
        { value: 'unit_asc', label: 'Unit Number (A-Z)' },
        { value: 'unit_desc', label: 'Unit Number (Z-A)' },
        { value: 'name_asc', label: 'Name (A-Z)' },
        { value: 'name_desc', label: 'Name (Z-A)' },
      ],
      showImportModal: false,
      showExportModal: false,
      localList: [],
      condoId: this.$route.params.condoId,
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      showDeleteModal: false,
      selectedResidentId: null,
      showEditModal: false,
      editingResident: null,
    };
  },
  computed: {
    ...mapState('condoMngt', ['residentList']),
  },
  methods: {
    ...mapActions('condoMngt', ['fetchResidentList']),
    useAlert,

    clearSearchQuery() {
      this.searchQuery = '';
      this.loadResidents();
    },

    handleDelete(residentId) {
      this.selectedResidentId = residentId;
      this.showDeleteModal = true;
    },

    handleEdit(resident) {
      this.editingResident = { ...resident };
      this.showEditModal = true;
    },

    async loadResidents() {
      this.selectedStatus = 'loading';
      try {
        const res = await this.fetchResidentList({
          condoId: this.condoId,
          query: this.searchQuery,
          page: this.currentPage,
          pageSize: this.pageSize,
        });

        this.localList = res.results || [];
        this.totalCount = res.total || 0;
        this.currentPage = res.page || 1;

        this.selectedStatus = this.localList.length ? 'list' : 'empty';
      } catch (err) {
        console.error(err);
        this.selectedStatus = 'error';
        this.useAlert('Failed to load residents');
      }
    },

    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.loadResidents();
    },
  },

  created() {
    this.debouncedSearch = debounce(() => {
      this.currentPage = 1;
      this.loadResidents();
    }, 400);
  },

  watch: {
    searchQuery() {
      this.debouncedSearch();
    },
  },

  mounted() {
    this.loadResidents();
  },
};
</script>

<template>
  <div>
    <!-- Modals -->
    <ImportResidentModal
      v-if="showImportModal"
      @close="showImportModal = false"
      @refresh="loadResidents"
    />

    <ExportResidentModal
      v-if="showExportModal"
      @close="showExportModal = false"
    />

    <DeleteResidentModal
      v-if="showDeleteModal"
      :condo-id="condoId"
      :resident-id="selectedResidentId"
      @close="showDeleteModal = false"
      @deleted="loadResidents"
    />

    <EditResidentModal
      v-if="showEditModal"
      :condo-id="condoId"
      :resident-data="editingResident"
      @close="showEditModal = false"
      @updated="loadResidents"
    />

    <!-- Search and Actions -->
    <div class="flex flex-col md:flex-row gap-4 mb-6 items-start md:items-center">
      <div class="relative flex-grow max-w-md">
        <div
          v-if="!searchQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10 pointer-events-none"
        >
          <fluent-icon icon="search" class="h-5 w-5 text-slate-400" />
        </div>

        <button
          v-if="searchQuery"
          @click="clearSearchQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center h-10"
        >
          <fluent-icon
            icon="dismiss"
            class="h-5 w-5 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
          />
        </button>

        <input
          type="text"
          class="bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-2 pl-10 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100"
          placeholder="Search residents..."
          v-model="searchQuery"
        />
      </div>

      <woot-button icon="upload" class="mb-4" @click="showImportModal = true">
        Import
      </woot-button>
      <woot-button
        icon="download"
        color-scheme="success"
        class="mb-4"
        @click="showExportModal = true"
      >
        Export
      </woot-button>
    </div>

    <!-- Error State -->
    <div
      v-if="selectedStatus === 'error'"
      class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center"
    >
      <fluent-icon icon="warning" size="48" class="text-red-500 dark:text-red-400" />
      <h1 class="text-slate-800 dark:text-slate-200">
        Error loading residents. Please try again later.
      </h1>
    </div>

    <!-- Empty State -->
    <div
      v-if="selectedStatus === 'empty'"
      class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col gap-4 items-center justify-center"
    >
      <fluent-icon
        icon="document"
        size="48"
        class="text-slate-600 dark:text-slate-400"
      />
      <h1 class="text-slate-800 dark:text-slate-200">
        No residents found.
      </h1>
    </div>

    <!-- Loading State -->
    <div
      v-if="selectedStatus === 'loading'"
      class="bg-slate-100 dark:bg-slate-800 rounded w-full min-h-[300px] flex flex-col items-center justify-center"
    >
      <LoadingState message="Loading residents..." />
    </div>

    <!-- Resident Table -->
    <table
      v-if="selectedStatus === 'list'"
      class="min-w-full border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900"
    >
      <thead class="bg-slate-100 dark:bg-slate-700">
        <tr>
          <th class="px-4 py-2 text-left text-slate-700 dark:text-slate-200">
            Name
          </th>
          <th class="px-4 py-2 text-left text-slate-700 dark:text-slate-200">
            Unit Number
          </th>
          <th class="px-4 py-2 text-left text-slate-700 dark:text-slate-200">
            Phone Number
          </th>
          <th class="px-4 py-2 text-left text-slate-700 dark:text-slate-200">
            Actions
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="resident in residentList"
          :key="resident.resident_id"
          class="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <td class="px-4 py-2 text-slate-800 dark:text-slate-100">
            {{ resident.name }}
          </td>
          <td class="px-4 py-2 text-slate-800 dark:text-slate-100">
            {{ resident.unit_number }}
          </td>
          <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
            {{ resident.phone_number }}
          </td>
          <td class="px-4 py-2 flex gap-4">
            <woot-button
              size="small"
              color-scheme="primary"
              icon="edit"
              @click="handleEdit(resident)"
            />
            <woot-button
              size="small"
              color-scheme="alert"
              icon="delete"
              @click="handleDelete(resident.resident_id)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <TableFooter
      :current-page="currentPage"
      :page-size="pageSize"
      :total-count="totalCount"
      @pageChange="handlePageChange"
    />
  </div>
</template>